import React, { useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, Print as PrintIcon } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import { useTranslation } from 'react-i18next';
import { A5_LANDSCAPE_PAGE_STYLE, A5_LANDSCAPE_PREVIEW_MAX_WIDTH, a5LandscapePrintSx } from '../utils/printPageStyles';
import {
  formatCargoCompanyLabel,
  generateBarcodeDataUrl,
  normalizeAddress,
  parseOrderAddress,
  parseOrderCargoInfo,
  resolveCargoBarcodeValue,
  toTurkishUpper,
} from '../utils/trendyolPrintUtils';

const TY_ORANGE = '#F27A1A';
const TY_ORANGE_DARK = '#e65100';
const STICKER_BORDER = '#000';

interface TrendyolCargoStickerModalProps {
  open: boolean;
  onClose: () => void;
  order: any;
  companyId?: string;
}

const TrendyolCargoStickerModal: React.FC<TrendyolCargoStickerModalProps> = ({ open, onClose, order }) => {
  const { t } = useTranslation('salesSystem');
  const printRef = useRef<HTMLDivElement>(null);
  const [isPreview, setIsPreview] = useState(true);

  const parsedShippingAddress = useMemo(() => parseOrderAddress(order?.shippingAddress), [order]);
  const parsedCargoInfo = useMemo(() => parseOrderCargoInfo(order?.cargoInfo), [order]);
  const shippingAddress = useMemo(() => normalizeAddress(parsedShippingAddress), [parsedShippingAddress]);

  const cargoBarcodeValue = useMemo(
    () => resolveCargoBarcodeValue(order, parsedCargoInfo),
    [order, parsedCargoInfo]
  );

  const horizontalBarcodeUrl = useMemo(
    () => (cargoBarcodeValue ? generateBarcodeDataUrl(cargoBarcodeValue, { height: 70, barWidth: 2 }) : ''),
    [cargoBarcodeValue]
  );

  const verticalBarcodeUrl = useMemo(
    () => (cargoBarcodeValue ? generateBarcodeDataUrl(cargoBarcodeValue, { height: 140, barWidth: 2 }) : ''),
    [cargoBarcodeValue]
  );

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Trendyol_Kargo_Sticker_${order?.orderNumber || order?.id || t('ecommerceOrders.marketplaces.unknown')}`,
    pageStyle: A5_LANDSCAPE_PAGE_STYLE,
    onBeforeGetContent: () => {
      setIsPreview(false);
      return Promise.resolve();
    },
    onAfterPrint: () => {
      setIsPreview(true);
    },
  });

  if (!order) return null;

  const fullName =
    shippingAddress?.name ||
    order.customerName ||
    t('ecommerceOrders.table.customerNameUnknown');
  const addressLine =
    shippingAddress?.address ||
    order.customerAddress ||
    t('ecommerceOrders.trendyolPrintModal.addressNotFound');
  const district = shippingAddress?.district || '';
  const city = shippingAddress?.city || '';
  const districtCitySlash = [district, city].filter(Boolean).join(' / ');
  const districtCityLine = [district, city].filter(Boolean).join(' ');

  const cargoCompany =
    parsedCargoInfo?.cargoProviderName ||
    parsedCargoInfo?.cargoCompany ||
    order.shippingCompany ||
    '';
  const cargoCompanyLabel = formatCargoCompanyLabel(cargoCompany);

  const stickerOrderNumber =
    order.shipmentPackageId ||
    order.trendyolOrderNumber ||
    order.orderNumber ||
    order.id ||
    '-';

  const stickerWidth = isPreview ? Math.min(360, A5_LANDSCAPE_PREVIEW_MAX_WIDTH) : A5_LANDSCAPE_PREVIEW_MAX_WIDTH;
  const boxTextSx = {
    fontWeight: 700,
    fontSize: isPreview ? '0.72rem' : '7pt',
    lineHeight: 1.35,
    fontFamily: 'Arial, sans-serif',
    color: '#000',
    wordBreak: 'break-word' as const,
  };

  const infoBoxSx = {
    border: `2px solid ${STICKER_BORDER}`,
    p: isPreview ? 1 : 0.75,
    mb: isPreview ? 1 : 0.75,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '60vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: TY_ORANGE,
          color: 'white',
        }}
      >
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <PrintIcon sx={{ mr: 1 }} />
          {t('ecommerceOrders.trendyolCargoStickerModal.title')}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: isPreview ? 3 : 0, display: 'flex', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
        <Box ref={printRef} sx={{ '@media print': { width: '100%', display: 'flex', justifyContent: 'center' } }}>
          <Box
            sx={{
              width: stickerWidth,
              bgcolor: '#fff',
              p: isPreview ? 1.5 : '3mm',
              mx: 'auto',
              ...a5LandscapePrintSx,
              '@media print': {
                ...a5LandscapePrintSx['@media print'],
                width: 'auto',
                maxWidth: '90mm',
                p: '2mm',
              },
            }}
          >
            <Box sx={{ textAlign: 'center', mb: isPreview ? 1 : 0.75 }}>
              {horizontalBarcodeUrl ? (
                <>
                  <Box
                    component="img"
                    src={horizontalBarcodeUrl}
                    alt={t('ecommerceOrders.trendyolPrintModal.cargoBarcodeAlt')}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      mx: 'auto',
                    }}
                  />
                  <Typography
                    sx={{
                      mt: 0.25,
                      fontWeight: 700,
                      fontSize: isPreview ? '0.85rem' : '8pt',
                      letterSpacing: '0.5px',
                      fontFamily: 'monospace',
                    }}
                  >
                    {cargoBarcodeValue}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t('ecommerceOrders.trendyolPrintModal.barcodeUnavailable')}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: isPreview ? 1 : 0.75, alignItems: 'stretch' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={infoBoxSx}>
                  <Typography sx={boxTextSx}>{toTurkishUpper(addressLine)}</Typography>
                  {districtCityLine && (
                    <Typography sx={{ ...boxTextSx, mt: 0.35 }}>
                      {toTurkishUpper(districtCityLine)}
                    </Typography>
                  )}
                  {districtCitySlash && (
                    <Typography sx={{ ...boxTextSx, mt: 0.35 }}>
                      {toTurkishUpper(districtCitySlash)}
                    </Typography>
                  )}
                </Box>

                <Box sx={infoBoxSx}>
                  <Typography sx={boxTextSx}>{toTurkishUpper(fullName)}</Typography>
                </Box>

                <Box sx={{ mt: isPreview ? 1.25 : 1 }}>
                  <Typography sx={{ ...boxTextSx, fontSize: isPreview ? '0.8rem' : '7.5pt' }}>
                    {stickerOrderNumber}
                  </Typography>
                  <Typography sx={{ ...boxTextSx, fontSize: isPreview ? '0.8rem' : '7.5pt', mt: 0.35 }}>
                    {cargoBarcodeValue || '-'}
                  </Typography>
                  {cargoCompanyLabel && (
                    <Typography
                      sx={{
                        ...boxTextSx,
                        fontSize: isPreview ? '0.82rem' : '7.5pt',
                        mt: 0.35,
                      }}
                    >
                      {cargoCompanyLabel}
                    </Typography>
                  )}
                </Box>
              </Box>

              {verticalBarcodeUrl && (
                <Box
                  sx={{
                    width: isPreview ? 56 : 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src={verticalBarcodeUrl}
                    alt={t('ecommerceOrders.trendyolCargoStickerModal.verticalBarcodeAlt')}
                    sx={{
                      height: isPreview ? 220 : 200,
                      width: 'auto',
                      transform: 'rotate(90deg)',
                      transformOrigin: 'center center',
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {isPreview && (
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button onClick={onClose} variant="outlined" startIcon={<CloseIcon />}>
            {t('ecommerceOrders.orderDetailsModal.close')}
          </Button>
          <Button
            onClick={handlePrint}
            variant="contained"
            startIcon={<PrintIcon />}
            sx={{
              bgcolor: TY_ORANGE,
              '&:hover': { bgcolor: TY_ORANGE_DARK },
            }}
          >
            {t('ecommerceOrders.trendyolCargoStickerModal.print')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default TrendyolCargoStickerModal;
